/**
 * The contact form's state and its one write (BON-472), split from the dialog
 * that draws it — the markup and the failure routing are separate jobs and the
 * routing is the one with a rule in it.
 *
 * THE RULE: EVERY FAILURE STAYS IN THE FORM. Whatever went wrong, the address
 * the CSR just typed is the one thing on screen that cannot be recovered by
 * reloading — they got it by reading it back to a customer on the phone — so
 * closing the form to show a message throws away the only irreplaceable part.
 * The message goes inline, beneath the fields, and Save comes back.
 *
 * That replaces a split where only a 400 stayed put. It was defensible for the
 * refusals that retyping cannot fix, and wrong for the one that a moment's wait
 * does fix — two CSRs colliding — which is exactly the case that asked the
 * customer to spell their address out again.
 */
import { useState } from 'react';

import { updateReservationContact, type ReservationRow } from '../../api/reservations';

import { describeRequestFailure } from './reservationRequestFailure';
import { contactUpdateOutcome, type RotationOutcome } from './reservationRotationOutcome';

export interface ReservationContactFormState {
  name: string;
  email: string;
  saving: boolean;
  /** Whatever went wrong, shown under the fields. Never a raw exception. */
  error: string | null;
  setName: (v: string) => void;
  setEmail: (v: string) => void;
  submit: (e: React.FormEvent) => Promise<void>;
}

export function useReservationContactForm(
  reservation: ReservationRow,
  /** Fired only when the save SUCCEEDED — the caller closes the form and owns
   *  the toast, the alert and the list refresh. */
  onDone: (outcome: RotationOutcome) => void,
  /** Re-read the list without closing the form. A refusal about the
   *  reservation's STATE means the row behind this modal is already out of date,
   *  and so does an attempt whose outcome we could not read. */
  onRefresh: () => void,
): ReservationContactFormState {
  const [name, setName] = useState(reservation.customerName ?? '');
  const [email, setEmail] = useState(reservation.customerEmail ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving) return;
    // Presence and shape only. Whether the address is DIFFERENT is the server's
    // question, and Save is deliberately never gated on the answer.
    if (name.trim().length === 0 || !/\S+@\S+/.test(email.trim())) {
      setError('A name and a valid email address are both required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const result = await updateReservationContact(reservation.id, {
        customerName: name.trim(),
        customerEmail: email.trim(),
      });
      onDone(contactUpdateOutcome(result));
    } catch (err) {
      const failure = describeRequestFailure('contact', err);
      setError(failure.message);
      setSaving(false);
      if (failure.refresh) onRefresh();
    }
  };

  return { name, email, saving, error, setName, setEmail, submit };
}
